import React, { useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import logo from '../Assets/logo.png'
import cart_icon from '../Assets/cart_icon.png'
import { ShopContext } from '../../Context/ShopContext'
import { backend_url } from '../../App'
import { getCategories } from '../../common/api/api'
import { getActiveCategories } from '../../common/categoryTree'

const DEFAULT_NAV_CATEGORIES = [
  {
    name: 'Laptops',
    slug: 'laptops',
    icon: '💻',
    subcategories: [{ name: 'Dell', slug: 'dell' }, { name: 'HP', slug: 'hp' }, { name: 'Lenovo', slug: 'lenovo' }],
  },
  {
    name: 'Mobiles',
    slug: 'mobiles',
    icon: '📱',
    subcategories: [{ name: 'Apple', slug: 'apple' }, { name: 'Samsung', slug: 'samsung' }, { name: 'Xiaomi', slug: 'xiaomi' }],
  },
  {
    name: 'Accessories',
    slug: 'accessories',
    icon: '🎧',
    subcategories: [{ name: 'AirPods', slug: 'airpods' }, { name: 'Earbuds', slug: 'earbuds' }, { name: 'Headphones', slug: 'headphones' }],
  },
]

const Navbar = () => {
  let [menu, setMenu] = useState("shop");
  const { getTotalCartItems } = useContext(ShopContext);
  const [accountDropdown, setAccountDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSubmenu, setMobileOpenSubmenu] = useState(null);
  const [navCategories, setNavCategories] = useState(DEFAULT_NAV_CATEGORIES);
  const [userRole, setUserRole] = useState(localStorage.getItem('user-role') || '');
  const [userProfilePicture, setUserProfilePicture] = useState(localStorage.getItem('user-profile-picture') || '');

  useEffect(() => {
    const loadCategories = () => {
      getCategories()
        .then((data) => {
          const active = getActiveCategories(data);
          if (!active.length) return;
          setNavCategories(
            active.map((cat, index) => ({
              name: cat.name,
              slug: cat.slug,
              icon: DEFAULT_NAV_CATEGORIES[index % DEFAULT_NAV_CATEGORIES.length]?.icon || '🛍️',
              subcategories: (cat.subcategories || []).filter((sub) => sub.active !== false),
            }))
          );
        })
        .catch(() => {});
    };

    loadCategories();
    window.addEventListener('focus', loadCategories);
    return () => window.removeEventListener('focus', loadCategories);
  }, []);

  // Always fetch the latest role from backend when logged in
  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (token) {
      fetch(`${backend_url}/getuser`, {
        headers: { 'auth-token': token },
      })
        .then(r => r.json())
        .then(data => {
          if (data.role) {
            localStorage.setItem('user-role', data.role);
            setUserRole(data.role);
          }
          if (data.profilePicture) {
            localStorage.setItem('user-profile-picture', data.profilePicture);
            setUserProfilePicture(data.profilePicture);
          } else {
            localStorage.removeItem('user-profile-picture');
            setUserProfilePicture('');
          }
        })
        .catch(() => {});
    } else {
      localStorage.removeItem('user-profile-picture');
      setUserProfilePicture('');
    }
  }, []);

  const accountRef = useRef();
  const searchRef = useRef();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Close search on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim().toLowerCase();
    if (!q) return;
    if (/laptop|dell|hp|lenovo|asus|acer|msi|macbook|microsoft/.test(q)) navigate('/laptops');
    else if (/phone|mobile|iphone|samsung|xiaomi|oppo|vivo|realme|nokia|infinix/.test(q)) navigate('/mobiles');
    else if (/watch|earb|headphone|airpod|charger|cable|power|cover|handfree/.test(q)) navigate('/accessories');
    else navigate('/laptops');
    setSearchQuery('');
    setSearchOpen(false);
  };

  const toggleMobileSubmenu = (name) => {
    setMobileOpenSubmenu(prev => prev === name ? null : name);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileOpenSubmenu(null);
  };

  const toggleAccountDropdown = () => {
    setAccountDropdown(!accountDropdown);
  };

  return (
    <>
    <div className='sticky top-0 z-[1000] flex items-center justify-between bg-[rgba(6,12,24,0.92)] h-[72px] px-[70px] border-b border-[rgba(0,212,255,0.12)] backdrop-blur-2xl shadow-[0_4px_32px_rgba(0,0,0,0.5)] max-[1100px]:px-[40px] max-[750px]:px-5' style={{ position: 'sticky' }}>
      {/* Top accent gradient line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00d4ff] to-[#7c3aed]" />

      {/* Logo */}
      <Link to='/' onClick={() => setMenu("shop")} className="flex items-center gap-2.5 flex-shrink-0">
        <img src={logo} alt="logo" className="h-11" />
        <p className="text-white font-black text-xl tracking-tight">⚡TechZone</p>
      </Link>

      {/* Desktop nav menu */}
      <ul className="flex list-none gap-8 items-center m-0 p-0 max-[750px]:hidden">
        <li className="relative text-[#a0aec0] text-[15px] font-semibold cursor-pointer hover:text-white transition-colors py-2" onClick={() => setMenu("shop")}>
          <Link to='/'>Home</Link>
          {menu === "shop" && <hr className="absolute -bottom-1 left-0 right-0 h-[3px] bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] border-none rounded-full" />}
        </li>
        {navCategories.map((category) => (
          <li key={category.slug} className="group relative text-[#a0aec0] text-[15px] font-semibold cursor-pointer hover:text-white transition-colors py-2" onClick={() => setMenu(category.slug)}>
            <Link to={`/category/${category.slug}`}>{category.name}</Link>
            {menu === category.slug && <hr className="absolute -bottom-1 left-0 right-0 h-[3px] bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] border-none rounded-full" />}
            {!!category.subcategories?.length && (
              <ul className="absolute top-full left-0 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 list-none m-0 p-2 min-w-[180px] z-[999] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] animate-dropdown-in" style={{ background: 'rgba(8,16,32,0.98)', border: '1px solid rgba(0,212,255,0.15)' }}>
                {category.subcategories.map((sub) => (
                  <li key={sub.slug} className="px-4 py-2 rounded-lg text-[#a0aec0] text-sm font-medium hover:text-[#00d4ff] hover:bg-[rgba(0,212,255,0.08)] transition-colors">
                    <Link to={`/category/${category.slug}?sub=${encodeURIComponent(sub.slug)}`}>{sub.name}</Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {/* Right side: search + account + cart + hamburger */}
      <div className="flex items-center gap-3">
        {/* Search bar */}
        <div ref={searchRef} className="relative flex items-center max-[750px]:hidden">
          <form onSubmit={handleSearch} className={`flex items-center overflow-hidden transition-all duration-300 rounded-xl ${searchOpen ? 'w-[220px] border border-[rgba(0,212,255,0.35)] bg-[rgba(0,212,255,0.05)]' : 'w-9 border border-[rgba(255,255,255,0.08)] bg-white/[0.04]'}`}>
            <button type="button" onClick={() => { setSearchOpen(s => !s); }} className="flex-shrink-0 w-9 h-9 flex items-center justify-center text-[#6b7a8d] hover:text-[#00d4ff] transition-colors cursor-pointer bg-transparent border-none">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </button>
            {searchOpen && (
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search products…"
                className="flex-1 h-9 bg-transparent border-none outline-none text-[#c0cce0] text-[13px] pr-3 placeholder:text-[#3d4f66] font-[inherit]"
              />
            )}
          </form>
        </div>
        {/* Account */}
        <div className="relative" ref={accountRef}>
          <button className="w-9 h-9 rounded-full flex items-center justify-center text-lg bg-white/[0.06] border border-[rgba(0,212,255,0.2)] text-[#a0aec0] cursor-pointer transition-all hover:border-[rgba(0,212,255,0.5)] hover:text-[#00d4ff] hover:bg-[rgba(0,212,255,0.1)]" onClick={toggleAccountDropdown} title="My Account">
            {localStorage.getItem('auth-token') && userProfilePicture ? (
              <img
                src={userProfilePicture}
                alt="profile"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              "👤"
            )}
          </button>
          {accountDropdown && (
            <div className="absolute top-[calc(100%+10px)] right-0 rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] min-w-[170px] z-[999] overflow-hidden animate-dropdown-in" style={{ background: 'rgba(8,16,32,0.98)', border: '1px solid rgba(0,212,255,0.15)' }}>
                {userRole === 'super-admin' && (
                  <Link to='/admin/dashboard' className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#00d4ff] hover:bg-[rgba(0,212,255,0.12)] transition-colors border-b border-white/[0.06]" onClick={() => setAccountDropdown(false)}>⚙ Admin Panel</Link>
                )}
              {localStorage.getItem('auth-token') ? (
                <>
                  <Link to='/profile' className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#a0aec0] hover:text-[#00d4ff] hover:bg-[rgba(0,212,255,0.08)] transition-colors border-b border-white/[0.06]" onClick={() => setAccountDropdown(false)}>👤 My Profile</Link>
                  <button className="w-full text-left px-4 py-3 text-sm font-medium text-[#a0aec0] hover:text-[#00d4ff] hover:bg-[rgba(0,212,255,0.08)] transition-colors cursor-pointer border-none bg-transparent font-[inherit]" onClick={() => {
                    localStorage.removeItem('auth-token');
                    localStorage.removeItem('user-role');
                    localStorage.removeItem('user-name');
                    localStorage.removeItem('user-profile-picture');
                    setUserRole('');
                    setUserProfilePicture('');
                    setAccountDropdown(false);
                    window.location.replace("/");
                  }}>Logout</button>
                </>
              ) : (
                <>
                  <Link to='/login' className="block px-4 py-3 text-sm font-medium text-[#a0aec0] hover:text-[#00d4ff] hover:bg-[rgba(0,212,255,0.08)] transition-colors" onClick={() => { setAccountDropdown(false); closeMobileMenu(); }}>Login</Link>
                </>
              )}
            </div>
          )}
        </div>

        {/* Desktop cart */}
        <Link to="/cart" className="relative flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.05] border border-[rgba(0,212,255,0.15)] text-[#a0aec0] text-sm font-semibold transition-all hover:border-[rgba(0,212,255,0.4)] hover:text-[#00d4ff] hover:bg-[rgba(0,212,255,0.08)] max-[750px]:hidden">
          <img src={cart_icon} alt="cart" className="w-5 brightness-0 invert opacity-70" />
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#7c3aed] text-[#060c18] text-xs font-extrabold flex items-center justify-center animate-pulse-badge">{getTotalCartItems()}</div>
        </Link>

        {/* Hamburger - mobile only */}
        <button
          className="hidden max-[750px]:flex flex-col gap-[5px] cursor-pointer p-2 bg-white/[0.04] border border-[rgba(0,212,255,0.15)] rounded-lg transition-colors hover:border-[rgba(0,212,255,0.4)]"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-5 h-0.5 bg-[#00d4ff] rounded transition-all duration-[350ms] origin-center ${mobileMenuOpen ? 'translate-y-[7px] rotate-45' : ''}`}></span>
          <span className={`block w-5 h-0.5 bg-[#00d4ff] rounded transition-all duration-[350ms] ${mobileMenuOpen ? 'opacity-0 scale-x-0' : ''}`}></span>
          <span className={`block w-5 h-0.5 bg-[#00d4ff] rounded transition-all duration-[350ms] origin-center ${mobileMenuOpen ? '-translate-y-[7px] -rotate-45' : ''}`}></span>
        </button>
      </div>

      </div>

      {/* Mobile overlay — must be OUTSIDE navbar div so backdrop-filter doesn't trap fixed positioning */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[1000] backdrop-blur-[2px] animate-overlay-in"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile side drawer */}
      <div
        className="fixed top-0 w-[290px] h-screen z-[1001] flex flex-col overflow-hidden"
        style={{
          right: mobileMenuOpen ? '0' : '-320px',
          transition: 'right 0.35s cubic-bezier(0.4,0,0.2,1)',
          background: 'linear-gradient(180deg, rgba(5,10,22,0.99) 0%, rgba(8,16,32,0.99) 100%)',
          borderLeft: '1px solid rgba(0,212,255,0.18)',
          boxShadow: '-10px 0 50px rgba(0,0,0,0.7)',
        }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(0,212,255,0.1)]" style={{ background: 'rgba(0,212,255,0.03)' }}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-badge-blink" />
            <span className="text-[#00d4ff] text-xs font-black tracking-[3px] uppercase">MENU</span>
          </div>
          <button className="w-8 h-8 rounded-full flex items-center justify-center text-[#6b7a8d] text-base border border-[rgba(0,212,255,0.12)] bg-white/[0.04] cursor-pointer transition-colors hover:text-[#00d4ff] hover:border-[rgba(0,212,255,0.4)]" onClick={closeMobileMenu} aria-label="Close menu">✕</button>
        </div>

        {/* Drawer menu */}
        <ul className="flex-1 list-none m-0 p-3 overflow-y-auto">
          {/* Home */}
          <li className="rounded-xl overflow-hidden mb-1">
            <Link to='/' className="flex items-center gap-3 px-4 py-3.5 text-[#c0cce0] text-[15px] font-semibold rounded-xl hover:text-white hover:bg-[rgba(0,212,255,0.08)] transition-colors" onClick={() => { setMenu("shop"); closeMobileMenu(); }}>
              <span className="text-lg">🏠</span>
              <span>Home</span>
            </Link>
          </li>

          {navCategories.map((category) => (
            <li key={category.slug} className="rounded-xl overflow-hidden mb-1">
              <div className="flex items-center justify-between">
                <Link to={`/category/${category.slug}`} className="flex items-center gap-3 px-4 py-3.5 text-[#c0cce0] text-[15px] font-semibold hover:text-white hover:bg-[rgba(0,212,255,0.08)] transition-colors flex-1 rounded-l-xl" onClick={() => { setMenu(category.slug); closeMobileMenu(); }}>
                  <span className="text-lg">{category.icon}</span>
                  <span>{category.name}</span>
                </Link>
                {!!category.subcategories?.length && (
                  <button className="px-3 py-3.5 text-[#6b7a8d] hover:text-[#00d4ff] transition-colors cursor-pointer border-none bg-transparent font-[inherit] text-xl leading-none" onClick={() => toggleMobileSubmenu(category.slug)} aria-label={`Expand ${category.name}`}>
                    <span className={`inline-block transition-transform duration-300 ${mobileOpenSubmenu === category.slug ? 'rotate-90' : ''}`}>›</span>
                  </button>
                )}
              </div>
              <ul className="list-none m-0 overflow-hidden" style={{ maxHeight: mobileOpenSubmenu === category.slug ? '420px' : '0', opacity: mobileOpenSubmenu === category.slug ? '1' : '0', transition: 'max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease' }}>
                {(category.subcategories || []).map((sub) => (
                  <li key={sub.slug} className="border-t border-[rgba(0,212,255,0.04)]">
                    <Link to={`/category/${category.slug}?sub=${encodeURIComponent(sub.slug)}`} className="block px-4 py-2.5 pl-[52px] text-[#7a8a9e] text-sm font-medium hover:text-[#00d4ff] hover:bg-[rgba(0,212,255,0.05)] transition-colors" onClick={closeMobileMenu}>{sub.name}</Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        {/* Cart at bottom of drawer */}
        <div className="p-4 border-t border-[rgba(0,212,255,0.1)]" style={{ background: 'rgba(0,212,255,0.02)' }}>
          <Link to="/cart" className="flex items-center justify-between px-4 py-3.5 rounded-xl bg-[rgba(0,212,255,0.08)] border border-[rgba(0,212,255,0.2)] text-[#00d4ff] text-sm font-semibold transition-all hover:bg-[rgba(0,212,255,0.15)]" onClick={closeMobileMenu}>
            <div className="flex items-center gap-2.5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <span>View Cart</span>
            </div>
            {getTotalCartItems() > 0 && (
              <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#7c3aed] text-[#060c18] text-xs font-extrabold flex items-center justify-center">{getTotalCartItems()}</span>
            )}
          </Link>
        </div>
      </div>
    </>
  )
}

export default Navbar
